import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdmGate from '@/pages/adm/components/AdmGate';
import AdmShell from '@/pages/adm/components/AdmShell';
import { listAdmAssignments } from '@/pages/adm/lib/admApi';
import type { AdmAssignmentRow } from '@/pages/adm/lib/admApi';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

export default function AdmOgPage() {
  return (
    <AdmGate requiredScope="og">
      {(auth) => (
        <AdmShell auth={auth} active="og">
          <OgPanel />
        </AdmShell>
      )}
    </AdmGate>
  );
}

function OgPanel() {
  const [assignments, setAssignments] = useState<AdmAssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await listAdmAssignments();
      setAssignments(all.filter((a) => a.scope === 'og' && a.status === 'active'));
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          OG 領域管理
        </h1>
        <p className="text-sm text-foreground-500">
          宇炁療育領域的 ADM 管理範圍：準施炁者名單、mentor_review、正式授權、停權、案例治理與受施者授權覆核。
        </p>
      </header>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground-800">OG 領域 ADM 名單</h2>
        {loading ? (
          <p className="text-sm text-foreground-400">載入中……</p>
        ) : error ? (
          <div className="space-y-2">
            <p className="text-sm text-rose-600">{error}</p>
            <button onClick={load} className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-sm cursor-pointer">
              重試
            </button>
          </div>
        ) : assignments.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-6 text-center">
            目前沒有啟用中的 OG 領域 ADM。
          </p>
        ) : (
          <div className="space-y-2">
            {assignments.map((a) => (
              <div key={a.id} className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground-800">{a.email || '（無信箱）'}</p>
                  <p className="text-xs text-foreground-400">授予於 {formatDate(a.granted_at)}</p>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">啟用</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground-800">治理入口</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/og/qi-healing/roles"
            className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-2 hover:border-accent-300 transition-colors"
          >
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-foreground-800">OG 施炁者培育與資格</h3>
              <i className="ri-arrow-right-up-line text-accent-500 text-base" />
            </div>
            <p className="text-xs text-foreground-500">管理 candidate／mentor_review／authorized／suspended 專業資格狀態。</p>
          </Link>
          <GovernanceEntry title="待覆核／待授權事項" desc="受施者授權與見證覆核佇列" ready={false} />
          <GovernanceEntry title="宇炁案例治理" desc="案例歸檔、爭議處理與追蹤管理" ready={false} />
          <GovernanceEntry title="7／30／90 日追蹤管理" desc="追蹤時程與完成狀態管理" ready={false} />
        </div>
      </section>
    </div>
  );
}

function GovernanceEntry({ title, desc, ready }: { title: string; desc: string; ready: boolean }) {
  return (
    <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-medium text-foreground-800">{title}</h3>
        {!ready && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">準備中</span>}
      </div>
      <p className="text-xs text-foreground-500">{desc}</p>
    </div>
  );
}